"use client";

import { useEffect, useRef, useState } from "react";

/**
 * Catches InvitationFetchError (thrown by page.tsx when the shared WP host
 * stalls past its 3-attempt retry budget) plus any other render-time crash
 * in a template. Without this boundary the guest saw Next's bare generic
 * "Application error: a client-side exception has occurred" — unbranded,
 * dead-end, and indistinguishable from a real bug to anyone reporting it.
 *
 * Retries are cheap here: `reset()` re-runs the whole server render, so a
 * WP stall that has cleared in the few seconds since the last attempt
 * usually just succeeds silently and the guest never sees this at all.
 */
const RETRY_DELAYS_MS = [1500, 3000, 6000, 10000];

export default function InvitationError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const [attempt, setAttempt] = useState(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    console.error("Invitation render error:", error);
  }, [error]);

  useEffect(() => {
    if (attempt >= RETRY_DELAYS_MS.length) return;
    timerRef.current = setTimeout(() => {
      setAttempt((n) => n + 1);
      reset();
    }, RETRY_DELAYS_MS[attempt]);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [attempt, reset]);

  const outOfRetries = attempt >= RETRY_DELAYS_MS.length;

  return (
    <div
      style={{
        minHeight: "100dvh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 20,
        padding: "32px 24px",
        textAlign: "center",
        background: "#FDF5E6",
        color: "#3D1C00",
        fontFamily: "var(--font-jakarta), system-ui, sans-serif",
      }}
    >
      <div
        aria-hidden="true"
        style={{
          width: 40,
          height: 40,
          borderRadius: "50%",
          border: "2.5px solid rgba(139, 26, 26, 0.18)",
          borderTopColor: "#8B1A1A",
          animation: outOfRetries ? "none" : "km-err-spin 0.9s linear infinite",
          opacity: outOfRetries ? 0.35 : 1,
        }}
      />

      <div style={{ maxWidth: 340 }}>
        <p
          style={{
            fontFamily: "var(--font-playfair), serif",
            fontSize: "1.15rem",
            fontWeight: 700,
            margin: "0 0 8px",
            color: "#8B1A1A",
          }}
        >
          {outOfRetries ? "Still reconnecting" : "Just a moment"}
        </p>
        <p style={{ fontSize: "0.9rem", lineHeight: 1.6, margin: 0, color: "#5C4033" }}>
          {outOfRetries
            ? "The invitation is taking longer than usual to load. Please check your connection and try again."
            : "Your invitation is loading — this can take a few extra seconds on some connections."}
        </p>
      </div>

      {outOfRetries && (
        <button
          type="button"
          onClick={() => {
            setAttempt(0);
            reset();
          }}
          style={{
            marginTop: 4,
            padding: "12px 32px",
            background: "#8B1A1A",
            color: "#FDF5E6",
            border: "none",
            borderRadius: 4,
            fontSize: "0.8rem",
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            cursor: "pointer",
          }}
        >
          Try Again
        </button>
      )}

      <style>{`
        @keyframes km-err-spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
